import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Brain, MessageSquare, Mic, Target, Shield, Clock, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import SeoHead from '../seo/SeoHead';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

const benefits = [
  {
    icon: Brain,
    title: 'Adapts to You',
    description: 'An AI life coach learns how you communicate and responds in a way that feels natural and personally relevant.'
  },
  {
    icon: MessageSquare,
    title: 'Always Available',
    description: 'Unlike traditional coaching sessions, AI coaching is available whenever you need guidance — late night, early morning, or during a busy day.'
  },
  {
    icon: Target,
    title: 'Goal-Focused',
    description: 'Set goals, track progress, and get accountability nudges that keep you moving forward without overwhelm.'
  },
  {
    icon: Shield,
    title: 'Private & Judgment-Free',
    description: 'Talk openly about your challenges in a secure space where you can reflect without fear of judgment.'
  },
  {
    icon: Clock,
    title: 'Fits Your Schedule',
    description: 'A five-minute check-in or a deep planning session — an AI coach scales to the time you have right now.'
  },
  {
    icon: Sparkles,
    title: 'Personality-Driven Insights',
    description: 'Understand your own traits and communication style so you can grow in a way that actually sticks.'
  }
];

const AILifeCoachGuide: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 text-white overflow-x-hidden">
      <SeoHead
        title="AI Life Coach — Personalized AI Coaching with Clarana"
        description="Discover how an AI life coach works, how it adapts to your personality, and why Clarana is an accessible alternative to traditional coaching."
        path="/guide/ai-life-coach"
      />

      {/* Animated Background Orbs */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-violet-500/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-purple-500/20 rounded-full blur-3xl animate-pulse delay-1000" />
        <div className="absolute top-1/2 right-1/3 w-64 h-64 bg-violet-600/10 rounded-full blur-3xl animate-pulse delay-500" />
      </div>

      {/* Navigation */}
      <nav className="relative z-10 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="text-2xl font-bold bg-gradient-to-r from-violet-400 to-purple-400 bg-clip-text text-transparent">
            Clarana
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/login">
              <Button variant="ghost" className="text-gray-300 hover:text-white hover:bg-white/10">
                Login
              </Button>
            </Link>
            <Link to="/signup">
              <Button className="bg-gradient-to-r from-violet-500 to-purple-500 hover:from-violet-600 hover:to-purple-600 text-white">
                Get Started
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative z-10 px-6 pt-16 pb-24">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-8"
          >
            <span className="inline-block px-4 py-1.5 rounded-full bg-white/10 border border-white/10 text-sm text-gray-300 mb-6">
              AI Coaching Guide
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              What Is an <span className="bg-gradient-to-r from-violet-400 to-purple-400 bg-clip-text text-transparent">AI Life Coach</span>?
            </h1>
            <p className="text-lg md:text-xl text-gray-300 max-w-2xl mx-auto">
              A modern path to personal growth: AI coaching that understands your personality, adapts to your communication style, and guides you toward your goals — on your schedule and at your pace.
            </p>
          </motion.div>

          <motion.div
            {...fadeInUp}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <Link to="/signup">
              <Button
                size="lg"
                className="bg-gradient-to-r from-violet-500 to-purple-500 hover:from-violet-600 hover:to-purple-600 text-white px-8 py-6 text-lg"
              >
                Try Clarana Free
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/pricing">
              <Button
                size="lg"
                variant="outline"
                className="border-white/20 bg-white/5 hover:bg-white/10 text-white px-8 py-6 text-lg"
              >
                View Plans
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <article className="relative z-10 px-6 py-16 bg-black/20">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="prose prose-lg prose-invert max-w-none"
          >
            <h2 className="text-3xl font-bold mb-4">AI Coaching Explained</h2>
            <p className="text-gray-300 leading-relaxed mb-6">
              An AI life coach is a digital companion that uses artificial intelligence to help you reflect on your goals, navigate challenges, and build better habits. Unlike a chatbot that gives generic advice, a true AI coaching platform learns from your inputs, understands your personality, and tailors its guidance to fit who you are and where you want to go.
            </p>
            <p className="text-gray-300 leading-relaxed mb-8">
              Traditional life coaching can be powerful, but it is also expensive, scheduled in advance, and limited by how often you can meet. AI coaching removes those barriers. It gives you a private, always-available space to think out loud, set goals, and receive consistent support — at a fraction of the cost.
            </p>

            <h2 className="text-3xl font-bold mb-4">How Does AI Coaching Work?</h2>
            <p className="text-gray-300 leading-relaxed mb-6">
              At the heart of AI coaching is a large language model trained to hold meaningful, empathetic conversations. But the technology alone is not what makes it useful. The real value comes from how the AI coach is designed around your growth journey.
            </p>
            <p className="text-gray-300 leading-relaxed mb-6">
              Here is how a thoughtful AI coaching platform like Clarana works:
            </p>
            <ol className="list-decimal list-inside space-y-4 text-gray-300 mb-8">
              <li>
                <strong>You share what is on your mind.</strong> Through text or voice, you describe a goal, a struggle, or a decision you are facing.
              </li>
              <li>
                <strong>The AI coach listens and adapts.</strong> It picks up on your tone, recurring themes, and communication style to respond in a way that feels right for you.
              </li>
              <li>
                <strong>You get personalized guidance.</strong> Instead of generic tips, you receive questions and suggestions that reflect your situation, values, and personality.
              </li>
              <li>
                <strong>You track progress over time.</strong> Goals, journal entries, and insights build into a clear picture of your growth.
              </li>
            </ol>

            <h2 className="text-3xl font-bold mb-4">Why AI Life Coaching Matters</h2>
            <p className="text-gray-300 leading-relaxed mb-8">
              Many people want the benefits of coaching but face real obstacles: cost, availability, stigma, or uncertainty about where to start. An AI life coach makes personal growth accessible. It is not a replacement for therapy or clinical mental health care, but it is an excellent bridge for people who want accountability, reflection, and structured self-improvement.
            </p>
          </motion.div>

          {/* Benefits Grid */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mt-12"
          >
            <h2 className="text-3xl font-bold mb-8 text-center">
              Benefits of an <span className="bg-gradient-to-r from-violet-400 to-purple-400 bg-clip-text text-transparent">AI Life Coach</span>
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {benefits.map((benefit) => (
                <div
                  key={benefit.title}
                  className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:bg-white/10 transition-colors group"
                >
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-violet-500/20 to-purple-500/20 flex items-center justify-center mb-4 group-hover:from-violet-500/30 group-hover:to-purple-500/30 transition-colors">
                    <benefit.icon className="w-6 h-6 text-violet-400" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">{benefit.title}</h3>
                  <p className="text-gray-400">{benefit.description}</p>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mt-16 prose prose-lg prose-invert max-w-none"
          >
            <h2 className="text-3xl font-bold mb-4">AI Coaching vs. Traditional Coaching</h2>
            <div className="overflow-x-auto mb-8">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="py-3 pr-4 text-gray-300 font-semibold">Factor</th>
                    <th className="py-3 pr-4 text-violet-300 font-semibold">AI Coaching</th>
                    <th className="py-3 text-gray-300 font-semibold">Traditional Coaching</th>
                  </tr>
                </thead>
                <tbody className="text-gray-300">
                  <tr className="border-b border-white/5">
                    <td className="py-3 pr-4">Availability</td>
                    <td className="py-3 pr-4">24/7</td>
                    <td className="py-3">Scheduled sessions</td>
                  </tr>
                  <tr className="border-b border-white/5">
                    <td className="py-3 pr-4">Cost</td>
                    <td className="py-3 pr-4">Affordable monthly plans</td>
                    <td className="py-3">Often $100+ per hour</td>
                  </tr>
                  <tr className="border-b border-white/5">
                    <td className="py-3 pr-4">Personalization</td>
                    <td className="py-3 pr-4">Adapts from every conversation</td>
                    <td className="py-3">Depends on coach style</td>
                  </tr>
                  <tr className="border-b border-white/5">
                    <td className="py-3 pr-4">Privacy</td>
                    <td className="py-3 pr-4">Private, secure app environment</td>
                    <td className="py-3">Varies by practice</td>
                  </tr>
                  <tr>
                    <td className="py-3 pr-4">Best for</td>
                    <td className="py-3 pr-4">Daily reflection, habit tracking, goal support</td>
                    <td className="py-3">Deep transformation, accountability partnerships</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h2 className="text-3xl font-bold mb-4">How Clarana Approaches AI Coaching</h2>
            <p className="text-gray-300 leading-relaxed mb-6">
              Clarana is built as an AI coaching platform, not just a chatbot. The experience starts with understanding you: your goals, your communication style, and your personality. From there, Clarana becomes a consistent partner in your growth.
            </p>
            <p className="text-gray-300 leading-relaxed mb-6">
              Key features include:
            </p>
            <ul className="list-disc list-inside space-y-4 text-gray-300 mb-8">
              <li>
                <strong>Adaptive conversations</strong> that evolve based on how you express yourself and what you prioritize.
              </li>
              <li>
                <strong>Voice chat</strong> for moments when speaking feels easier than typing.
              </li>
              <li>
                <strong>Personality insights</strong> that help you understand your natural tendencies and blind spots.
              </li>
              <li>
                <strong>Goal tracking and journaling</strong> to turn insight into action.
              </li>
              <li>
                <strong>Progress analytics</strong> that show patterns over time.
              </li>
            </ul>

            <h2 className="text-3xl font-bold mb-4">Is an AI Life Coach Right for You?</h2>
            <p className="text-gray-300 leading-relaxed mb-6">
              If you are looking for a supportive, accessible way to grow, an AI life coach could be a great fit. It is especially useful if you want regular check-ins, help organizing your thoughts, or a private space to explore goals without a big financial commitment.
            </p>
            <p className="text-gray-300 leading-relaxed mb-8">
              It is not a substitute for therapy, counseling, or medical advice. If you are dealing with a mental health condition, a licensed professional is the right resource. But for everyday growth, clarity, and accountability, AI coaching is a meaningful new option.
            </p>
          </motion.div>

          {/* CTA Section */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mt-16 bg-gradient-to-br from-violet-500/20 to-purple-500/20 backdrop-blur-sm border border-violet-500/30 rounded-2xl p-8 md:p-12 text-center"
          >
            <h2 className="text-2xl md:text-3xl font-bold mb-4">
              Start Your AI Coaching Journey Today
            </h2>
            <p className="text-gray-300 mb-8 max-w-xl mx-auto">
              Try Clarana free and experience how an AI life coach can help you grow, reflect, and reach your goals.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/signup">
                <Button
                  size="lg"
                  className="bg-gradient-to-r from-violet-500 to-purple-500 hover:from-violet-600 hover:to-purple-600 text-white px-8 py-6 text-lg"
                >
                  Get Started Free
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/pricing">
                <Button
                  size="lg"
                  variant="outline"
                  className="border-white/20 bg-white/5 hover:bg-white/10 text-white px-8 py-6 text-lg"
                >
                  Compare Plans
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </article>

      {/* Footer */}
      <footer className="relative z-10 px-6 py-12 border-t border-white/10">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-2xl font-bold bg-gradient-to-r from-violet-400 to-purple-400 bg-clip-text text-transparent">
              Clarana
            </div>
            <div className="flex items-center gap-8">
              <Link to="/login" className="text-gray-400 hover:text-white transition-colors">
                Login
              </Link>
              <Link to="/signup" className="text-gray-400 hover:text-white transition-colors">
                Sign Up
              </Link>
              <Link to="/pricing" className="text-gray-400 hover:text-white transition-colors">
                Pricing
              </Link>
            </div>
            <div className="text-gray-500 text-sm">
              © {new Date().getFullYear()} Clarana. All rights reserved.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AILifeCoachGuide;
